'use client'
import { sendGTMEvent } from '@next/third-parties/google'

import { UserIdEnum } from '@/enums/UserIdEnum'
import Events from '@/interfaces/EventLoggerEvents'
import { env } from '@/lib/env'
import { eventBus } from '@/lib/eventBus/eventBus'
import { PRODUCT_PAGE_ITEM_INDEX } from '@/lib/eventBus/selectedItemList'
import { formatProductToGTM } from '@/lib/eventBus/subscribers/formatProductToGTM'
import { toGA4ItemList } from '@/lib/eventBus/subscribers/ga4ItemLists'
import { TrackingTopic } from '@/lib/eventBus/trackingTopics'

import type {
  CartItemAddedPayload,
  CartItemRemovedPayload,
  CartLineItem,
  ItemListViewedPayload,
  ItemSelectedPayload,
  ItemViewedPayload,
  ListContext,
  ListItem,
  OrderPlacedPayload,
  SearchPerformedPayload,
  TrackingContext,
  TrackingErrorPayload,
  TrackingEvent,
} from '@/lib/eventBus/trackingEvents'
import type { GTMDataLayer, GTMEcommerceEvent } from '@/types/gtm'

const GTM_CONFIG = {
  // AZ_ENV (not NODE_ENV) is the real production discriminator: Next.js builds preview/staging with
  // NODE_ENV=production too, so gating sends on NODE_ENV would have leaked staging traffic into real GA4.
  enabled: env.AZ_ENV === 'p',
  // Non-production always logs so QA can verify tracking end-to-end without a real send — see below.
  debug: env.AZ_ENV !== 'p' || env.NEXT_PUBLIC_GTM_DEBUG,
}

const debugLog = (fn: () => void) => {
  if (GTM_CONFIG.debug) {
    fn()
  }
}

const formatRole = ({ roles, isAnonymous }: TrackingContext) =>
  `RoleValue (${roles?.join(', ') || (isAnonymous ? 'anonymous' : 'unknown')})`

const ISO_CODE_BY_SITE_LANGUAGE: Partial<Record<string, string>> = { dk: 'da' }

const toIsoLanguage = (siteLanguage: string) => ISO_CODE_BY_SITE_LANGUAGE[siteLanguage] ?? siteLanguage

const getLanguage = ({ isAnonymous, siteLanguage, userLanguage }: TrackingContext) => {
  if (!isAnonymous) {
    return userLanguage ?? null
  }

  return siteLanguage ? toIsoLanguage(siteLanguage) : null
}

const buildDataLayer = async (
  event: Events,
  context: TrackingContext,
  ecommerce?: GTMEcommerceEvent,
  customData?: Record<string, unknown>
): Promise<GTMDataLayer> => {
  const uid = await context.hashedUid.catch(() => UserIdEnum.Anonymous)

  return {
    event,
    ecommerce: { ...ecommerce },
    custom_data: { ...customData },
    userData: {
      uid,
      customer_id: context.customerId ?? null,
      user_segment: context.userSegment || context.siteContext || null,
      role: formatRole(context),
      market: context.market ?? null,
      customer_segment_1: context.customerSegment1 ?? null,
      customer_segment_2: context.customerSegment2 ?? null,
      language: getLanguage(context),
    },
    page_data: {
      page_title: context.page.title,
      page_location: context.page.location,
      page_path: context.page.path,
      content_group1: context.siteContext ?? null,
      content_group2: context.siteLanguage ?? null,
    },
  }
}

const send = async (
  event: Events,
  context: TrackingContext,
  ecommerce?: GTMEcommerceEvent,
  customData?: Record<string, unknown>
) => {
  const dataLayer = await buildDataLayer(event, context, ecommerce, customData)

  debugLog(() => {
    console.groupCollapsed(`[GA4] Event: ${event}${GTM_CONFIG.enabled ? '' : ' (not sent — non-production)'}`)
    console.warn('Event Data:', dataLayer)
    console.groupEnd()
  })

  if (!GTM_CONFIG.enabled) {
    return
  }

  if (ecommerce) {
    // Clears the previous ecommerce object first — GTM's recommended dataLayer reset so ecommerce
    // fields don't leak between events (e.g. a purchase's items lingering on the next page_view).
    sendGTMEvent({ ecommerce: null })
  }

  sendGTMEvent(dataLayer)
}

const roundToCents = (amount: number) => Math.round(amount * 100) / 100

const roundAmount = (amount?: number | null) => (amount == null ? null : roundToCents(amount))

const withListIndex = <Item extends object>(item: Item | null, index?: number) =>
  item && { ...item, index: index ?? null }

const buildLineItemsEcommerceEvent = (
  data: { items: CartLineItem[]; listContext?: ListContext },
  currency: TrackingContext['currency']
): GTMEcommerceEvent | null => {
  const itemList = toGA4ItemList(data.listContext)
  const items = data.items
    .map(({ product, priceData, quantity, index }) =>
      withListIndex(formatProductToGTM(product, priceData, quantity, itemList, currency), index)
    )
    .filter((item) => item != null)

  if (!items.length) {
    return null
  }

  const totalPrice = data.items.reduce((sum, { priceData }) => sum + (priceData?.totalPrice ?? 0), 0)

  return { currency: items[0].currency, value: roundToCents(totalPrice), ...itemList, items }
}

const sendCartEvent =
  (event: Events) =>
  async ({ context, data }: TrackingEvent<CartItemAddedPayload | CartItemRemovedPayload>) => {
    const ecommerce = buildLineItemsEcommerceEvent(data, context.currency)

    if (!ecommerce) {
      return
    }

    await send(event, context, ecommerce)
  }

const handleCartItemAdded = sendCartEvent(Events.AddToCart)

const handleCartItemRemoved = sendCartEvent(Events.RemoveFromCart)

const handleItemListViewed = async ({ context, data }: TrackingEvent<ItemListViewedPayload>) => {
  const itemList = toGA4ItemList(data.listContext)
  const items = data.items
    .map(({ product, priceData, index }) =>
      withListIndex(formatProductToGTM(product, priceData, 1, itemList, context.currency), index)
    )
    .filter((item) => item != null)

  if (!items.length) {
    return
  }

  await send(Events.ViewItemList, context, {
    currency: items[0].currency,
    ...itemList,
    items,
  })
}

const sendSingleItemEvent = async (
  event: Events,
  context: TrackingContext,
  { product, priceData, index }: ListItem,
  listContext: ListContext
) => {
  const itemList = toGA4ItemList(listContext)
  const item = withListIndex(formatProductToGTM(product, priceData, 1, itemList, context.currency), index)

  if (!item) {
    return
  }

  await send(event, context, {
    currency: item.currency,
    value: item.price,
    ...itemList,
    items: [item],
  })
}

const handleItemViewed = async ({ context, data }: TrackingEvent<ItemViewedPayload>) => {
  await sendSingleItemEvent(Events.ViewItem, context, { ...data, index: PRODUCT_PAGE_ITEM_INDEX }, data.listContext)
}

const handleItemSelected = async ({ context, data }: TrackingEvent<ItemSelectedPayload>) => {
  await sendSingleItemEvent(Events.SelectItem, context, data.item, data.listContext)
}

const handleUserLoggedIn = async ({ context }: TrackingEvent<null>) => {
  await send(Events.LoginEvent, context)
}

const handleOrderPlaced = async ({ context, data }: TrackingEvent<OrderPlacedPayload>) => {
  const { order, items } = data
  const ecommerce = buildLineItemsEcommerceEvent({ items }, context.currency)

  if (!ecommerce) {
    console.warn('[GA4] Purchase event: invalid order data')

    return
  }

  await send(Events.PurchaseEvent, context, {
    ...ecommerce,
    transaction_id: order.code ?? null,
    payment_method: order.paymentMode?.code ?? null,
    shipping: roundAmount(order.deliveryCost?.value),
    tax: roundAmount(order.totalTax?.value),
  })
}

const handlePageViewed = async ({ context }: TrackingEvent<null>) => {
  await send(Events.PageView, context)
}

const handleSearchPerformed = async ({ context, data }: TrackingEvent<SearchPerformedPayload>) => {
  await send(Events.Search, context, undefined, { search_term: data.searchTerm })
}

const handleTrackingError = async ({ context, data }: TrackingEvent<TrackingErrorPayload>) => {
  await send(Events.Error, context, undefined, {
    error_message: data.error.message,
    error_stack: data.error.stack ?? null,
    error_context: data.context ?? null,
    fatal: false,
  })
}

export const registerGA4Subscriber = () => {
  const subscriptions = [
    eventBus.subscribe(TrackingTopic.CartItemAdded, handleCartItemAdded),
    eventBus.subscribe(TrackingTopic.CartItemRemoved, handleCartItemRemoved),
    eventBus.subscribe(TrackingTopic.ItemListViewed, handleItemListViewed),
    eventBus.subscribe(TrackingTopic.ItemViewed, handleItemViewed),
    eventBus.subscribe(TrackingTopic.ItemSelected, handleItemSelected),
    eventBus.subscribe(TrackingTopic.UserLoggedIn, handleUserLoggedIn),
    eventBus.subscribe(TrackingTopic.OrderPlaced, handleOrderPlaced),
    eventBus.subscribe(TrackingTopic.PageViewed, handlePageViewed),
    eventBus.subscribe(TrackingTopic.SearchPerformed, handleSearchPerformed),
    eventBus.subscribe(TrackingTopic.TrackingErrorOccurred, handleTrackingError),
  ]

  return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
}
